import React, { useState, useEffect } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	TextField,
	MenuItem,
	FormControl,
	InputLabel,
	Select,
	Box,
	Typography,
	Chip,
	Alert,
	Autocomplete,
	CircularProgress,
	Grid,
} from "@mui/material";
import { X, Send } from "lucide-react";

const SendNotificationModal = ({
	open,
	onClose,
	supabase,
	currentUser,
	preselectedRecipient = null,
	userSchoolIds = [], // Add this prop to filter schools
}) => {
	const [formData, setFormData] = useState({
		recipientType: "",
		specificUsers: [],
		roleType: "",
		schoolId: "",
		subject: "",
		message: "",
	});
	const [users, setUsers] = useState([]);
	const [schools, setSchools] = useState([]);
	const [loading, setLoading] = useState(false);
	const [sending, setSending] = useState(false);
	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");

	const recipientTypes = [
		{ value: "individual", label: "Individual User" },
		{ value: "multiple", label: "Multiple Users" },
		{ value: "role", label: "By Role" },
		{ value: "school", label: "School Teachers" },
	];

	const roleTypes = [
		{ value: "ROLE_SCHOOL_TEACHER", label: "Teachers" },
		{ value: "ROLE_FUNMATH_ADMIN", label: "Funmath Admins" },
		{ value: "ROLE_FUNMIYO_EMPLOYEE", label: "Funmiyo Employees" },
		{ value: "ROLE_SCHOOL_ADMIN", label: "School Admins" },
	];

	// Initialize form data when modal opens or preselected recipient changes
	useEffect(() => {
		if (preselectedRecipient && open) {
			if (preselectedRecipient.recipientType === "school") {
				setFormData({
					recipientType: "school",
					specificUsers: [],
					roleType: "",
					schoolId: preselectedRecipient.schoolId || preselectedRecipient.id,
					subject: "",
					message: "",
				});
			} else {
				setFormData({
					recipientType: "individual",
					specificUsers: [preselectedRecipient],
					roleType: "",
					schoolId: "",
					subject: "",
					message: "",
				});
			}
		} else if (open && !preselectedRecipient) {
			// If userSchoolIds is provided, default to school mode
			// Otherwise, reset to empty form
			if (userSchoolIds && userSchoolIds.length > 0) {
				setFormData({
					recipientType: "school",
					specificUsers: [],
					roleType: "",
					schoolId: "",
					subject: "",
					message: "",
				});
			} else {
				setFormData({
					recipientType: "",
					specificUsers: [],
					roleType: "",
					schoolId: "",
					subject: "",
					message: "",
				});
			}
		}
	}, [preselectedRecipient, open]); // Removed userSchoolIds from dependency array

	useEffect(() => {
		if (open) {
			fetchUsers();
			fetchSchools();
		}
	}, [open]);

	const fetchUsers = async () => {
		try {
			setLoading(true);
			const { data, error } = await supabase
				.from("profiles")
				.select("id, full_name, email, role")
				.eq("status", "Active")
				.neq("id", currentUser.id);

			if (error) throw error;
			setUsers(data || []);
		} catch (err) {
			console.error("Error fetching users:", err);
			setError("Failed to load users");
		} finally {
			setLoading(false);
		}
	};

	const fetchSchools = async () => {
		try {
			let query = supabase
				.from("schools")
				.select("id, name")
				.eq("status", "Active");

			// If userSchoolIds is provided, filter schools to only those assigned to the user
			if (userSchoolIds && userSchoolIds.length > 0) {
				query = query.in("id", userSchoolIds);
			}

			const { data, error } = await query;

			if (error) throw error;
			setSchools(data || []);
		} catch (err) {
			console.error("Error fetching schools:", err);
			setError("Failed to load schools");
		}
	};

	const resetForm = () => {
		setFormData({
			recipientType: "",
			specificUsers: [],
			roleType: "",
			schoolId: "",
			subject: "",
			message: "",
		});
		setError("");
		setSuccess("");
	};

	const handleClose = () => {
		resetForm();
		onClose();
	};

	const getRecipientIds = async () => {
		const { recipientType, specificUsers, roleType, schoolId } = formData;
		let recipientIds = [];

		switch (recipientType) {
			case "individual":
				recipientIds = [specificUsers[0]?.id];
				break;

			case "multiple":
				recipientIds = specificUsers.map((user) => user.id);
				break;

			case "role":
				const { data: roleUsers, error: roleError } = await supabase
					.from("profiles")
					.select("id")
					.eq("role", roleType)
					.eq("status", "Active")
					.neq("id", currentUser.id);

				if (roleError) throw roleError;
				recipientIds = roleUsers.map((user) => user.id);
				break;

			case "school":
				const { data: schoolTeachers, error: schoolError } = await supabase
					.from("profiles")
					.select("id")
					.eq("role", "ROLE_SCHOOL_TEACHER")
					.eq("school_id", schoolId)
					.eq("status", "Active")
					.neq("id", currentUser.id);

				if (schoolError) throw schoolError;
				recipientIds = schoolTeachers.map((user) => user.id);
				break;

			default:
				throw new Error("Please select a recipient type");
		}

		return recipientIds.filter((id) => id);
	};

	const handleSend = async () => {
		try {
			setSending(true);
			setError("");

			if (!formData.subject.trim()) {
				throw new Error("Subject is required");
			}
			if (!formData.message.trim()) {
				throw new Error("Message is required");
			}

			const recipientIds = await getRecipientIds();

			if (recipientIds.length === 0) {
				throw new Error("No recipients found for the selected criteria");
			}

			const notifications = recipientIds.map((recipientId) => ({
				sender_id: currentUser.id,
				recipient_id: recipientId,
				subject: formData.subject.trim(),
				message: formData.message.trim(),
				type: "admin_message",
				is_read: false,
			}));

			const { error } = await supabase
				.from("notifications")
				.insert(notifications);

			if (error) throw error;

			setSuccess(
				`Notification sent to ${recipientIds.length} recipient(s) successfully!`
			);

			setTimeout(() => {
				handleClose();
			}, 1500);
		} catch (err) {
			console.error("Error sending notification:", err);
			setError(err.message || "Failed to send notification");
		} finally {
			setSending(false);
		}
	};

	const isFormValid = () => {
		const {
			recipientType,
			specificUsers,
			roleType,
			schoolId,
			subject,
			message,
		} = formData;

		if (!subject.trim() || !message.trim()) return false;

		switch (recipientType) {
			case "individual":
				return specificUsers.length === 1;
			case "multiple":
				return specificUsers.length > 1;
			case "role":
				return roleType !== "";
			case "school":
				return schoolId !== "";
			default:
				return false;
		}
	};

	const getSelectedSchoolName = () => {
		if (preselectedRecipient?.recipientType === "school") {
			return preselectedRecipient.name;
		}
		const selectedSchool = schools.find(
			(school) => school.id === formData.schoolId
		);
		return selectedSchool?.name || "";
	};

	const getRecipientDisplayText = () => {
		if (preselectedRecipient?.recipientType === "school") {
			return `All teachers in ${preselectedRecipient.name}`;
		}
		if (preselectedRecipient && !preselectedRecipient.recipientType) {
			return `${preselectedRecipient.full_name || preselectedRecipient.name} (${
				preselectedRecipient.email
			})`;
		}
		return null;
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					pb: 1,
				}}>
				<Typography variant="h6" sx={{ fontWeight: 600 }}>
					Send Notification
				</Typography>
				<Button
					onClick={handleClose}
					size="small"
					sx={{ minWidth: "auto", p: 0.5 }}>
					<X size={18} />
				</Button>
			</DialogTitle>

			<DialogContent sx={{ pt: 1 }}>
				{error && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{error}
					</Alert>
				)}

				{success && (
					<Alert severity="success" sx={{ mb: 2 }}>
						{success}
					</Alert>
				)}

				<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
					{/* Show preselected recipient info */}
					{preselectedRecipient && (
						<Box sx={{ p: 1.5, bgcolor: "grey.100", borderRadius: 1 }}>
							<Typography variant="body2" color="text.secondary">
								Sending to:
							</Typography>
							<Typography variant="body1" sx={{ fontWeight: 500 }}>
								{getRecipientDisplayText()}
							</Typography>
						</Box>
					)}

					{/* Recipient type selection - show simplified version if userSchoolIds provided */}
					{!preselectedRecipient &&
					userSchoolIds &&
					userSchoolIds.length > 0 ? (
						// Simplified version for school-only mode (when opened from header)
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<Box
									sx={{
										p: 1.5,
										bgcolor: "primary.50",
										borderRadius: 1,
										border: 1,
										borderColor: "primary.200",
									}}>
									<Typography
										variant="body2"
										color="primary.main"
										sx={{ fontWeight: 500 }}>
										📚 Sending to: School Teachers
									</Typography>
									<Typography variant="caption" color="text.secondary">
										Select a school to send notifications to all teachers in
										that school
									</Typography>
								</Box>
							</Grid>

							{/* School selection */}
							<Grid item xs={12}>
								<Autocomplete
									size="small"
									value={
										schools.find((school) => school.id === formData.schoolId) ||
										null
									}
									onChange={(event, newValue) => {
										setFormData({
											...formData,
											schoolId: newValue ? newValue.id : "",
										});
									}}
									options={schools}
									getOptionLabel={(option) => option.name}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Select School"
											placeholder="Search schools..."
											required
										/>
									)}
									isOptionEqualToValue={(option, value) =>
										option.id === value.id
									}
									loading={loading}
								/>
							</Grid>
						</Grid>
					) : !preselectedRecipient ? (
						// Full version with all recipient type options (when opened normally)
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<FormControl fullWidth size="small">
									<InputLabel>Recipient Type</InputLabel>
									<Select
										value={formData.recipientType}
										label="Recipient Type"
										onChange={(e) =>
											setFormData({
												...formData,
												recipientType: e.target.value,
												specificUsers: [],
												roleType: "",
												schoolId: "",
											})
										}>
										{recipientTypes.map((type) => (
											<MenuItem key={type.value} value={type.value}>
												{type.label}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>

							{/* Conditional recipient selection */}
							{(formData.recipientType === "individual" ||
								formData.recipientType === "multiple") && (
								<Grid item xs={12}>
									<Autocomplete
										size="small"
										multiple={formData.recipientType === "multiple"}
										value={
											formData.recipientType === "multiple"
												? formData.specificUsers
												: formData.specificUsers[0] || null
										}
										onChange={(event, newValue) => {
											if (formData.recipientType === "multiple") {
												setFormData({
													...formData,
													specificUsers: newValue || [],
												});
											} else {
												setFormData({
													...formData,
													specificUsers: newValue ? [newValue] : [],
												});
											}
										}}
										options={users}
										getOptionLabel={(option) =>
											`${option.full_name} (${option.email})`
										}
										renderInput={(params) => (
											<TextField
												{...params}
												label={
													formData.recipientType === "multiple"
														? "Select Users"
														: "Select User"
												}
												placeholder="Search..."
											/>
										)}
										renderTags={(value, getTagProps) =>
											value.map((option, index) => (
												<Chip
													key={option.id}
													label={option.full_name}
													{...getTagProps({ index })}
													size="small"
												/>
											))
										}
										loading={loading}
									/>
								</Grid>
							)}

							{formData.recipientType === "role" && (
								<Grid item xs={12}>
									<FormControl fullWidth size="small">
										<InputLabel>Role</InputLabel>
										<Select
											value={formData.roleType}
											label="Role"
											onChange={(e) =>
												setFormData({ ...formData, roleType: e.target.value })
											}>
											{roleTypes.map((role) => (
												<MenuItem key={role.value} value={role.value}>
													{role.label}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</Grid>
							)}

							{formData.recipientType === "school" && (
								<Grid item xs={12}>
									<Autocomplete
										size="small"
										value={
											schools.find(
												(school) => school.id === formData.schoolId
											) || null
										}
										onChange={(event, newValue) => {
											setFormData({
												...formData,
												schoolId: newValue ? newValue.id : "",
											});
										}}
										options={schools}
										getOptionLabel={(option) => option.name}
										renderInput={(params) => (
											<TextField
												{...params}
												label="School"
												placeholder="Search schools..."
												required
											/>
										)}
										isOptionEqualToValue={(option, value) =>
											option.id === value.id
										}
										loading={loading}
									/>
								</Grid>
							)}
						</Grid>
					) : null}

					{/* Subject and Message - more compact */}
					<TextField
						label="Subject"
						value={formData.subject}
						onChange={(e) =>
							setFormData({ ...formData, subject: e.target.value })
						}
						fullWidth
						size="small"
						required
					/>

					<TextField
						label="Message"
						value={formData.message}
						onChange={(e) =>
							setFormData({ ...formData, message: e.target.value })
						}
						fullWidth
						multiline
						rows={3}
						size="small"
						required
					/>
				</Box>
			</DialogContent>

			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button onClick={handleClose} disabled={sending} size="small">
					Cancel
				</Button>
				<Button
					onClick={handleSend}
					variant="contained"
					disabled={!isFormValid() || sending}
					startIcon={
						sending ? <CircularProgress size={14} /> : <Send size={16} />
					}
					size="small">
					{sending ? "Sending..." : "Send"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default SendNotificationModal;
